import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import UnifiedNavHeader from "../../../../components/navigation/UnifiedNavHeader";
import { Header } from "../../../../components/dashboards/components/shared/Header";
import { FOOTBALL_HEADER_COPY } from "../constants/headerContent";
import DesktopNavigation from "../components/DesktopNavigation";
import FixturesView from "../components/FixturesView";
import StandingsView from "../components/StandingsView";
import PlayersView from "../components/PlayersView";
import {
  useRTDBStandings,
  useRTDBTopScorers as useTopScorers,
  useRTDBOdds
} from "../rtdb";
import { getAvailableSeasons } from "../../../../components/dashboards/utils/footballLeagueUtils";
import { FOOTBALL_TABS, DEFAULT_GAMES_PAGINATION, DEFAULT_PLAYERS_PAGINATION } from "../constants/filters";
import { useQuickPrediction } from "../../../../components/dashboards/hooks/useQuickPrediction";
import FixtureOddsModal from "../components/FixtureOddsModal";
import DesktopRailNavigation from "../components/desktop/DesktopRailNavigation";
import { LeagueModal, MarketModal, SortModal } from "../components/desktop/modals";
import { getBrazilianStatus } from "../../../../utils/detectBrazil";
import { footballDashboardPtBR } from "../../../../components/mappings/sports-dash-mappings/football-dashboard.pt-br";
import FootballPredictionControlBar from "../components/FootballPredictionControlBar";
import { DatePickerModal } from "../../../../components/dashboards/components/shared/date-picker";
import { ensureISODate } from "../../../../components/dashboards/utils/dateUtils";
import { useFootballFixtures, useFootballDashboardFilters } from "../hooks";

const FootballDesktopDashboard = () => {
  const seasons = useMemo(() => getAvailableSeasons(), []);
  const isBrazilian = getBrazilianStatus();
  const t = useCallback(
    (text) => (isBrazilian ? footballDashboardPtBR[text] || text : text),
    [isBrazilian]
  );

  const [activeTab, setActiveTab] = useState(FOOTBALL_TABS.FIXTURES);
  const [fixturesPagination, setFixturesPagination] = useState(DEFAULT_GAMES_PAGINATION);
  const [playersPagination, setPlayersPagination] = useState(DEFAULT_PLAYERS_PAGINATION);
  const [selectedFixtureForOdds, setSelectedFixtureForOdds] = useState(null);
  const [predictionRequestId, setPredictionRequestId] = useState(0);
  const activePredictionRequestRef = useRef(null);
  const [predictionFlowActive, setPredictionFlowActive] = useState(false);
  const cacheCheckKeyRef = useRef(null);

  // Separate season state for Standings and Players tabs
  const [standingsSeason, setStandingsSeason] = useState(new Date().getFullYear().toString());
  const [playersSeason, setPlayersSeason] = useState(new Date().getFullYear().toString());

  // --- Shared filter state (centralized normalization) ---
  const {
    selectedDate,
    selectedLeague,
    todayDate,
    railFilters,
    railActiveTab,
    railHasActiveFilters,
    railPrimaryTabs,
    railFilterObjects,
    handleRailTabChange,
    handleFilterClick,
    updateRailFilter,
    resetRailFilters,
    showDateSheet: showDateModal,
    setShowDateSheet: setShowDateModal,
    showLeagueSheet: showLeagueModal,
    setShowLeagueSheet: setShowLeagueModal,
    showMarketSheet: showMarketModal,
    setShowMarketSheet: setShowMarketModal,
    showSortSheet: showSortModal,
    setShowSortSheet: setShowSortModal,
  } = useFootballDashboardFilters();

  // --- Shared fixtures data pipeline (centralized fetch + filter + sort) ---
  const {
    fixtures: sortedFixtures,
    loading: fixturesLoading,
    error: fixturesError,
    refetch: refetchFixtures,
  } = useFootballFixtures({
    date: selectedDate,
    league: selectedLeague,
    market: railFilters?.market || "all",
    sort: railFilters?.sort || "smart",
    enabled: activeTab === FOOTBALL_TABS.FIXTURES,
  });

  const {
    isLoading: isPredicting,
    selectedGames: selectedGamesForPrediction,
    usageState,
    error: predictionError,
    toggleGameSelection,
    clearSelections,
    predictSelectedGames,
    getGamePrediction,
    isGameSelected,
    selectionCount,
    isGameFrozen,
    checkCachedPredictions
  } = useQuickPrediction();

  const standingsQuery = useRTDBStandings({
    league: selectedLeague,
    season: standingsSeason,
    enabled: activeTab === FOOTBALL_TABS.STANDINGS && !!selectedLeague
  });

  const playersQuery = useTopScorers({
    league: selectedLeague,
    season: playersSeason,
    enabled: activeTab === FOOTBALL_TABS.PLAYERS && !!selectedLeague
  });

  const oddsQuery = useRTDBOdds({
    fixtureId: selectedFixtureForOdds?.fixture?.id,
    enabled: !!selectedFixtureForOdds,
    realtime: true
  });

  // Reset pagination when filters change
  useEffect(() => {
    setFixturesPagination((prev) => ({ ...prev, currentPage: 1 }));
  }, [selectedDate, selectedLeague]);

  useEffect(() => {
    setPlayersPagination((prev) => ({ ...prev, currentPage: 1 }));
  }, [selectedLeague, playersSeason]);

  // Prime predictions from cache/Firestore when fixtures change
  useEffect(() => {
    if (!sortedFixtures?.length) return;
    const gameIds = sortedFixtures
      .map((fixture) => fixture.fixture?.id || fixture.id)
      .filter(Boolean);
    if (!gameIds.length) return;
    const key = gameIds.slice().sort().join(",");
    if (cacheCheckKeyRef.current === key) return;
    cacheCheckKeyRef.current = key;
    checkCachedPredictions?.("football", gameIds).catch((err) =>
      console.error("checkCachedPredictions failed (Football desktop):", err)
    );
  }, [sortedFixtures, checkCachedPredictions]);

  const handleFixturesPageChange = useCallback((page) => {
    setFixturesPagination((prev) => ({
      ...prev,
      currentPage: Math.max(1, Math.min(page, prev.totalPages))
    }));
  }, []);

  const handlePlayersPageChange = useCallback((page) => {
    setPlayersPagination((prev) => ({
      ...prev,
      currentPage: Math.max(1, Math.min(page, prev.totalPages))
    }));
  }, []);

  const handlePredictRequest = useCallback(async () => {
    setPredictionRequestId((prev) => {
      const next = prev + 1;
      activePredictionRequestRef.current = next;
      return next;
    });
    setPredictionFlowActive(true);
    await predictSelectedGames("football");
    clearSelections(); // hide bar and reset counts immediately
    setPredictionFlowActive(false);
  }, [clearSelections, predictSelectedGames]);

  const handleViewOdds = useCallback((fixture) => {
    setSelectedFixtureForOdds(fixture);
  }, []);

  const handleCloseOddsModal = useCallback(() => {
    setSelectedFixtureForOdds(null);
  }, []);

  // Pagination sync
  useEffect(() => {
    setFixturesPagination((prev) => {
      const totalPages = Math.max(1, Math.ceil((sortedFixtures?.length || 0) / prev.perPage));
      return { ...prev, totalPages, currentPage: Math.min(prev.currentPage, totalPages) };
    });
  }, [sortedFixtures]);

  useEffect(() => {
    setPlayersPagination((prev) => {
      const totalPages = Math.max(1, Math.ceil((playersQuery.data?.length || 0) / prev.perPage));
      return { ...prev, totalPages, currentPage: Math.min(prev.currentPage, totalPages) };
    });
  }, [playersQuery.data]);

  const handleRefresh = useCallback(() => {
    if (activeTab === FOOTBALL_TABS.FIXTURES) refetchFixtures?.();
    if (activeTab === FOOTBALL_TABS.STANDINGS) standingsQuery.refetch?.();
    if (activeTab === FOOTBALL_TABS.PLAYERS) playersQuery.refetch?.();
  }, [activeTab, refetchFixtures, playersQuery, standingsQuery]);

  const formatTime = useCallback((value) => {
    if (!value) return "";
    const date = new Date(value);
    return date.toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: true
    });
  }, []);

  // Auto-reset selections when a prediction request finishes
  useEffect(() => {
    if (isPredicting) return;

    const shouldFinalize =
      !isPredicting &&
      !predictionFlowActive &&
      activePredictionRequestRef.current === predictionRequestId &&
      predictionRequestId > 0;

    if (shouldFinalize) {
      clearSelections();
      activePredictionRequestRef.current = null;
      setTimeout(() => clearSelections(), 600);
    }
  }, [clearSelections, isPredicting, predictionFlowActive, predictionRequestId]);

  return (
    <div className="min-h-screen bg-black text-white">
      <UnifiedNavHeader title="Football Dashboard" showBackButton />
      <div className="relative px-6 py-8 pb-24">
        <div className="max-w-[1600px] mx-auto space-y-4">
          <Header t={t} {...FOOTBALL_HEADER_COPY} />

          {/* Desktop Rail Navigation - Filter Rail */}
          <DesktopRailNavigation
            sport="football"
            primaryTabs={railPrimaryTabs}
            filters={railFilterObjects}
            activeTab={railActiveTab}
            onTabChange={handleRailTabChange}
            onFilterChange={handleFilterClick}
            onReset={resetRailFilters}
            hasActiveFilters={railHasActiveFilters}
          />

          <DesktopNavigation activeTab={activeTab} onTabChange={setActiveTab} />

          {activeTab === FOOTBALL_TABS.FIXTURES && (
            <FixturesView
              fixtures={sortedFixtures}
              loading={fixturesLoading}
              error={fixturesError}
              onRetry={refetchFixtures}
              pagination={fixturesPagination}
              onPageChange={handleFixturesPageChange}
              onToggleSelection={toggleGameSelection}
              isGameSelected={isGameSelected}
              isGameFrozen={isGameFrozen}
              getGamePrediction={getGamePrediction}
              formatTime={formatTime}
              onViewOdds={handleViewOdds}
            />
          )}

          {activeTab === FOOTBALL_TABS.STANDINGS && (
            <StandingsView
              standings={standingsQuery.data || []}
              loading={standingsQuery.loading}
              error={standingsQuery.error}
              onRetry={standingsQuery.refetch}
              seasons={seasons}
              selectedLeague={selectedLeague}
              selectedSeason={standingsSeason}
              onSeasonChange={setStandingsSeason}
            />
          )}

          {activeTab === FOOTBALL_TABS.PLAYERS && (
            <PlayersView
              players={playersQuery.data || []}
              loading={playersQuery.loading}
              error={playersQuery.error}
              onRetry={playersQuery.refetch}
              pagination={playersPagination}
              onPageChange={handlePlayersPageChange}
              seasons={seasons}
              selectedLeague={selectedLeague}
              selectedSeason={playersSeason}
              onSeasonChange={setPlayersSeason}
            />
          )}
        </div>
      </div>
      {selectedFixtureForOdds && (
        <FixtureOddsModal
          fixture={selectedFixtureForOdds}
          odds={oddsQuery.data}
          loading={oddsQuery.loading}
          onClose={handleCloseOddsModal}
        />
      )}

      {/* Desktop Filter Modals */}
      <DatePickerModal
        isOpen={showDateModal}
        onClose={() => setShowDateModal(false)}
        selectedDate={ensureISODate(railFilters.date || selectedDate)}
        onDateSelect={(isoDate) => {
          updateRailFilter("date", ensureISODate(isoDate));
          setShowDateModal(false);
        }}
      />

      <LeagueModal
        isOpen={showLeagueModal}
        onClose={() => setShowLeagueModal(false)}
        selectedLeague={String(railFilters.league || 'all')}
        onLeagueSelect={(leagueId) => {
          updateRailFilter('league', String(leagueId));
          setShowLeagueModal(false);
        }}
      />

      <MarketModal
        isOpen={showMarketModal}
        onClose={() => setShowMarketModal(false)}
        currentMarket={railFilters.market || 'all'}
        onMarketChange={(market) => {
          updateRailFilter('market', market);
          setShowMarketModal(false);
        }}
      />

      <SortModal
        isOpen={showSortModal}
        onClose={() => setShowSortModal(false)}
        currentSort={railFilters.sort || 'smart'}
        onSortChange={(sort) => {
          updateRailFilter('sort', sort);
          setShowSortModal(false);
        }}
      />

      <FootballPredictionControlBar
        selectionCount={selectionCount}
        usageState={usageState}
        isLoading={isPredicting}
        error={predictionError}
        onPredict={handlePredictRequest}
        onClear={clearSelections}
        selectedGames={selectedGamesForPrediction || []}
      />
    </div>
  );
};

export default FootballDesktopDashboard;
